import { PostHogProvider } from '@posthog/react'
import { PostHog } from 'posthog-js'
import { useContext, useEffect, useState, type ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { AuthContext } from '@/context/auth-context'
import { startFeatureFlagClient } from './client'
import { FeatureFlagLoadState, FeatureFlagLoadStateContext } from './context'
import { featureFlagDefaults } from './definitions'
import { clearFeatureFlagContext } from './sentry'

/** Auth changes replace requests, SDK state, and evaluated Sentry context together. */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { user } = useContext(AuthContext)
  if (Object.keys(featureFlagDefaults).length === 0) return children
  return (
    <UserFeatureFlagsProvider key={user?.uid ?? 'signed-out'} user={user}>
      {children}
    </UserFeatureFlagsProvider>
  )
}

function UserFeatureFlagsProvider({ user, children }: { user: User | null; children: ReactNode }) {
  // The flags API rejects anonymous sessions, so they keep frontend defaults until the account is linked.
  const flagUser = user && !user.isAnonymous ? user : null
  const [client, setClient] = useState(() => new PostHog())
  const [loadState, setLoadState] = useState(flagUser ? FeatureFlagLoadState.LOADING : FeatureFlagLoadState.UNAVAILABLE)
  // Linking keeps the uid, so this provider is not remounted. Switch to loading in the linking render
  // so consumers never read the anonymous defaults as a decision for the permanent account.
  const [loadStateIsForFlagUser, setLoadStateIsForFlagUser] = useState(flagUser != null)
  if (loadStateIsForFlagUser !== (flagUser != null)) {
    setLoadStateIsForFlagUser(flagUser != null)
    setLoadState(flagUser ? FeatureFlagLoadState.LOADING : FeatureFlagLoadState.UNAVAILABLE)
  }

  useEffect(() => {
    clearFeatureFlagContext()
    if (!flagUser) return
    const nextClient = new PostHog()
    const stop = startFeatureFlagClient(nextClient, flagUser, setLoadState)
    setClient(nextClient)
    return () => {
      stop()
      clearFeatureFlagContext()
    }
  }, [flagUser])

  return (
    <PostHogProvider client={client}>
      <FeatureFlagLoadStateContext.Provider value={loadState}>{children}</FeatureFlagLoadStateContext.Provider>
    </PostHogProvider>
  )
}
